/**
 * Deterministic SEO "ideal blog post anatomy" scorer.
 *
 * Parses generated blog HTML in the browser (no extra dependencies, no API
 * cost) and grades it against the 10-part ideal anatomy that the generation
 * prompts in blogSeoPrompts.js already aim for:
 *
 *   1. Title tag        6. Formatting
 *   2. Meta description 7. Internal links
 *   3. Hook / AEO       8. Conclusion / CTA
 *   4. Table of contents 9. FAQ
 *   5. Body structure   10. Schema markup
 *
 * The scoring is heuristic and transparent: every section reports the
 * individual checks behind its score so the UI can explain exactly how the
 * number was produced. Subjective qualities ("sales pitch" meta, keyword
 * naturalness) are approximated with length/structure/token-overlap proxies.
 */

const SECTIONS = [
  { id: 'title', title: 'Title tag', weight: 10 },
  { id: 'meta', title: 'Meta description', weight: 10 },
  { id: 'hook', title: 'Hook / direct answer', weight: 15 },
  { id: 'toc', title: 'Table of contents', weight: 8 },
  { id: 'body', title: 'Body structure', weight: 20 },
  { id: 'formatting', title: 'Paragraphs & formatting', weight: 12 },
  { id: 'internalLinks', title: 'Internal linking', weight: 8 },
  { id: 'conclusion', title: 'Conclusion / CTA', weight: 7 },
  { id: 'faq', title: 'FAQ section', weight: 12 },
  { id: 'schema', title: 'Schema markup', weight: 8 },
];

const TOC_IDS = ['table-of-contents', 'inhoudsopgave'];
const INTERNAL_LINK_IDS = ['internal-links', 'interne-links'];
const FAQ_IDS = ['faq'];
const CONCLUSION_IDS = ['conclusion', 'conclusie'];
const REFERENCE_IDS = ['references', 'bronnen'];
const STRUCTURAL_SECTION_IDS = [
  ...INTERNAL_LINK_IDS,
  ...FAQ_IDS,
  ...CONCLUSION_IDS,
  ...REFERENCE_IDS,
];

const STOP_WORDS = new Set([
  // English
  'the', 'a', 'an', 'of', 'to', 'for', 'and', 'or', 'in', 'on', 'with', 'your',
  'you', 'how', 'what', 'why', 'is', 'are', 'best', 'guide', 'top', 'vs', 'that',
  'this', 'from', 'into', 'about', 'can', 'will', 'should', 'when', 'where',
  // Dutch
  'de', 'het', 'een', 'van', 'en', 'of', 'in', 'op', 'met', 'je', 'jouw', 'hoe',
  'wat', 'waarom', 'zijn', 'beste', 'gids', 'top', 'voor', 'naar', 'over', 'die',
  'dat', 'deze', 'kan', 'wanneer', 'waar',
]);

const QUESTION_STARTERS = [
  'what', 'how', 'why', 'when', 'where', 'who', 'which',
  'wat', 'hoe', 'waarom', 'wanneer', 'waar', 'wie', 'welke',
];

const CTA_PHRASES = [
  'contact', 'get started', 'sign up', 'try', 'download', 'subscribe',
  'learn more', 'discover', 'start', 'book', 'request', 'explore',
  'neem contact', 'aan de slag', 'meld je aan', 'probeer', 'ontdek',
  'begin', 'vraag', 'bekijk', 'lees meer', 'schrijf je in',
];

const SCHEMA_TYPES = ['Article', 'FAQPage', 'Person', 'BreadcrumbList'];

const PASS = 'pass';
const WARN = 'warn';
const FAIL = 'fail';

function countWords(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function countSentences(text) {
  const matches = (text || '').match(/[^.!?]+[.!?]+/g);
  if (matches && matches.length) return matches.length;
  return (text || '').trim() ? 1 : 0;
}

function normalize(text) {
  return (text || '').toLowerCase();
}

/**
 * Significant keyword tokens derived from the H1 headline.
 */
export function extractKeywordTokens(headline) {
  return normalize(headline)
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((token) => token.length >= 3 && !STOP_WORDS.has(token));
}

function keywordCoverage(text, tokens) {
  if (!tokens.length) return { ratio: 1, hits: 0 };
  const haystack = normalize(text);
  const hits = tokens.filter((token) => haystack.includes(token)).length;
  return { ratio: hits / tokens.length, hits };
}

function isQuestionHeading(text) {
  const clean = (text || '').trim().toLowerCase();
  if (!clean) return false;
  if (clean.endsWith('?')) return true;
  const firstWord = clean.split(/\s+/)[0];
  return QUESTION_STARTERS.includes(firstWord);
}

function makeCheck(id, label, status, detail, points, maxPoints) {
  return { id, label, status, detail, points, maxPoints };
}

function buildSection(meta, checks) {
  const maxPoints = checks.reduce((sum, c) => sum + c.maxPoints, 0);
  const points = checks.reduce((sum, c) => sum + c.points, 0);
  const ratio = maxPoints > 0 ? points / maxPoints : 0;
  const score = Math.round(meta.weight * ratio * 100) / 100;
  let status = FAIL;
  if (ratio >= 0.85) status = PASS;
  else if (ratio >= 0.5) status = WARN;
  return {
    id: meta.id,
    title: meta.title,
    score,
    maxScore: meta.weight,
    status,
    checks,
  };
}

function getDocument(html) {
  if (typeof DOMParser === 'undefined') return null;
  try {
    return new DOMParser().parseFromString(html || '', 'text/html');
  } catch (err) {
    return null;
  }
}

function isInStructuralBlock(el) {
  const navToc = el.closest('nav[id]');
  if (navToc && TOC_IDS.includes(navToc.id)) return true;
  const section = el.closest('section[id]');
  if (section && STRUCTURAL_SECTION_IDS.includes(section.id)) return true;
  return false;
}

function findSectionByIds(doc, ids) {
  for (const id of ids) {
    const el = doc.getElementById(id);
    if (el) return el;
  }
  return null;
}

function scoreTitle(title, tokens) {
  const len = title.length;
  let lenStatus = FAIL;
  let lenPoints = 0;
  if (!title) {
    lenStatus = FAIL;
    lenPoints = 0;
  } else if (len <= 60) {
    lenStatus = PASS;
    lenPoints = 6;
  } else if (len <= 70) {
    lenStatus = WARN;
    lenPoints = 3;
  }
  const lenDetail = title
    ? `Title is ${len} characters (ideal \u226460, max 70).`
    : 'No <title> tag found in the output.';

  const firstHalf = title.slice(0, Math.ceil(title.length / 2));
  const inFirstHalf = keywordCoverage(firstHalf, tokens).hits > 0;
  const anywhere = keywordCoverage(title, tokens).hits > 0;
  let kwStatus = FAIL;
  let kwPoints = 0;
  let kwDetail = 'Primary keyword not found in the title.';
  if (inFirstHalf) {
    kwStatus = PASS;
    kwPoints = 4;
    kwDetail = 'Primary keyword appears near the front of the title.';
  } else if (anywhere) {
    kwStatus = WARN;
    kwPoints = 2;
    kwDetail = 'Primary keyword is present but not near the front of the title.';
  }

  return buildSection(SECTIONS[0], [
    makeCheck('title-length', 'Length within 60\u201370 characters', lenStatus, lenDetail, lenPoints, 6),
    makeCheck('title-keyword', 'Primary keyword placed early', kwStatus, kwDetail, kwPoints, 4),
  ]);
}

function scoreMeta(description, tokens) {
  const len = description.length;
  let lenStatus = FAIL;
  let lenPoints = 0;
  if (!description) {
    lenStatus = FAIL;
  } else if (len >= 150 && len <= 160) {
    lenStatus = PASS;
    lenPoints = 6;
  } else if ((len >= 140 && len < 150) || (len > 160 && len <= 169)) {
    lenStatus = WARN;
    lenPoints = 3;
  }
  const lenDetail = description
    ? `Meta description is ${len} characters (ideal 150\u2013160).`
    : 'No meta description found in the output.';

  const present = keywordCoverage(description, tokens).hits > 0;
  return buildSection(SECTIONS[1], [
    makeCheck('meta-length', 'Length within 150\u2013160 characters', lenStatus, lenDetail, lenPoints, 6),
    makeCheck(
      'meta-keyword',
      'Primary keyword present',
      present ? PASS : FAIL,
      present ? 'Primary keyword appears in the meta description.' : 'Primary keyword not found in the meta description.',
      present ? 4 : 0,
      4,
    ),
  ]);
}

function scoreHook(doc, tokens) {
  const h1 = doc.querySelector('h1');
  let hook = null;
  if (h1) {
    let sibling = h1.nextElementSibling;
    while (sibling && sibling.tagName !== 'P') {
      sibling = sibling.nextElementSibling;
    }
    hook = sibling;
  }
  if (!hook) hook = doc.body ? doc.body.querySelector('p') : null;
  const text = hook ? hook.textContent : '';
  const words = countWords(text);

  let lenStatus = FAIL;
  let lenPoints = 0;
  if (words >= 100 && words <= 150) {
    lenStatus = PASS;
    lenPoints = 7;
  } else if ((words >= 80 && words < 100) || (words > 150 && words <= 170)) {
    lenStatus = WARN;
    lenPoints = 4;
  }
  const lenDetail = hook
    ? `Opening paragraph is ${words} words (ideal 100\u2013150).`
    : 'No opening hook paragraph found after the H1.';

  const sentences = countSentences(text);
  let sentStatus = FAIL;
  let sentPoints = 0;
  if (sentences >= 2 && sentences <= 6) {
    sentStatus = PASS;
    sentPoints = 4;
  } else if (sentences === 1 || (sentences > 6 && sentences <= 8)) {
    sentStatus = WARN;
    sentPoints = 2;
  }
  const sentDetail = hook
    ? `Hook opens with ${sentences} sentence${sentences === 1 ? '' : 's'} (a concise 2\u20133 sentence answer is ideal).`
    : 'No hook paragraph to evaluate.';

  const present = keywordCoverage(text, tokens).hits > 0;
  return buildSection(SECTIONS[2], [
    makeCheck('hook-length', 'Hook is 100\u2013150 words', lenStatus, lenDetail, lenPoints, 7),
    makeCheck('hook-answer', 'Opens with a concise direct answer', sentStatus, sentDetail, sentPoints, 4),
    makeCheck(
      'hook-keyword',
      'Primary keyword in the hook',
      present ? PASS : FAIL,
      present ? 'Primary keyword appears in the opening hook.' : 'Primary keyword not found in the hook.',
      present ? 4 : 0,
      4,
    ),
  ]);
}

function scoreToc(doc) {
  let nav = null;
  for (const id of TOC_IDS) {
    const el = doc.getElementById(id);
    if (el && el.tagName === 'NAV') { nav = el; break; }
  }
  if (!nav) {
    const navs = doc.querySelectorAll('nav[id]');
    navs.forEach((n) => { if (!nav && TOC_IDS.includes(n.id)) nav = n; });
  }
  const presentStatus = nav ? PASS : FAIL;
  const presentDetail = nav
    ? 'A table-of-contents navigation block is present.'
    : 'No table of contents found after the hook.';

  const anchors = nav ? nav.querySelectorAll('a[href^="#"]') : [];
  const anchorCount = anchors.length;
  let anchorStatus = FAIL;
  let anchorPoints = 0;
  if (anchorCount >= 2) {
    anchorStatus = PASS;
    anchorPoints = 4;
  } else if (anchorCount === 1) {
    anchorStatus = WARN;
    anchorPoints = 2;
  }
  const anchorDetail = nav
    ? `Contains ${anchorCount} anchor link${anchorCount === 1 ? '' : 's'} to body sections.`
    : 'No anchor links available.';

  return buildSection(SECTIONS[3], [
    makeCheck('toc-present', 'Table of contents present', presentStatus, presentDetail, nav ? 4 : 0, 4),
    makeCheck('toc-anchors', 'Links to body sections', anchorStatus, anchorDetail, anchorPoints, 4),
  ]);
}

function getBodyHeadings(doc) {
  const all = Array.from(doc.querySelectorAll('h2'));
  return all.filter((h2) => !isInStructuralBlock(h2));
}

function scoreBody(doc) {
  const bodyH2 = getBodyHeadings(doc);
  const count = bodyH2.length;

  let countStatus = FAIL;
  let countPoints = 0;
  if (count >= 5 && count <= 8) {
    countStatus = PASS;
    countPoints = 8;
  } else if ((count >= 3 && count < 5) || (count > 8 && count <= 10)) {
    countStatus = WARN;
    countPoints = 4;
  }
  const countDetail = `${count} body section heading${count === 1 ? '' : 's'} (ideal 5\u20138 H2 sections).`;

  const withId = bodyH2.filter((h2) => h2.id).length;
  const idRatio = count ? withId / count : 0;
  let idStatus = FAIL;
  let idPoints = 0;
  if (idRatio >= 0.85) { idStatus = PASS; idPoints = 4; }
  else if (idRatio >= 0.5) { idStatus = WARN; idPoints = 2; }
  const idDetail = count
    ? `${withId} of ${count} body headings have an id for TOC anchoring.`
    : 'No body headings to anchor.';

  const h3Count = Array.from(doc.querySelectorAll('h3')).filter((h3) => !isInStructuralBlock(h3)).length;
  const h3Status = h3Count >= 1 ? PASS : FAIL;
  const h3Detail = `${h3Count} supporting H3 heading${h3Count === 1 ? '' : 's'} found in the body.`;

  const questionCount = bodyH2.filter((h2) => isQuestionHeading(h2.textContent)).length;
  const questionRatio = count ? questionCount / count : 0;
  let qStatus = FAIL;
  let qPoints = 0;
  if (questionRatio >= 0.3) { qStatus = PASS; qPoints = 3; }
  else if (questionCount >= 1) { qStatus = WARN; qPoints = 1; }
  const qDetail = `${questionCount} of ${count} headings use question phrasing (helps featured snippets).`;

  const h4Count = doc.querySelectorAll('h4').length;
  let h4Status = PASS;
  let h4Points = 2;
  if (h4Count > 4) { h4Status = WARN; h4Points = 0; }
  else if (h4Count > 2) { h4Status = WARN; h4Points = 1; }
  const h4Detail = `${h4Count} H4 heading${h4Count === 1 ? '' : 's'} (avoid nesting deeper than H3).`;

  return buildSection(SECTIONS[4], [
    makeCheck('body-h2-count', '5\u20138 H2 body sections', countStatus, countDetail, countPoints, 8),
    makeCheck('body-h2-ids', 'Headings have anchor ids', idStatus, idDetail, idPoints, 4),
    makeCheck('body-h3', 'Uses H3 for sub-points', h3Status, h3Detail, h3Count >= 1 ? 3 : 0, 3),
    makeCheck('body-questions', 'Question-style headings', qStatus, qDetail, qPoints, 3),
    makeCheck('body-h4', 'Avoids excessive H4 nesting', h4Status, h4Detail, h4Points, 2),
  ]);
}

function scoreFormatting(doc) {
  const paragraphs = Array.from(doc.querySelectorAll('p')).filter((p) => !isInStructuralBlock(p));
  const shortParas = paragraphs.filter((p) => countSentences(p.textContent) <= 3).length;
  const shortRatio = paragraphs.length ? shortParas / paragraphs.length : 0;
  let paraStatus = FAIL;
  let paraPoints = 0;
  if (shortRatio >= 0.7) { paraStatus = PASS; paraPoints = 5; }
  else if (shortRatio >= 0.4) { paraStatus = WARN; paraPoints = 2; }
  const paraDetail = paragraphs.length
    ? `${shortParas} of ${paragraphs.length} paragraphs are 3 sentences or fewer.`
    : 'No body paragraphs found.';

  const lists = Array.from(doc.querySelectorAll('ul, ol')).filter((l) => !isInStructuralBlock(l));
  const hasTable = !!doc.querySelector('table');
  const listStatus = lists.length >= 1 ? PASS : FAIL;
  const listDetail = `${lists.length} list${lists.length === 1 ? '' : 's'}${hasTable ? ' and a comparison table' : ''} used for scannable content.`;

  // Each body list should have at most 3 items.
  const listsOverCap = lists.filter((l) => l.querySelectorAll(':scope > li').length > 3);
  let listCapStatus = PASS;
  let listCapPoints = 3;
  if (listsOverCap.length > 0) { listCapStatus = WARN; listCapPoints = 1; }
  const listCapDetail = listsOverCap.length === 0
    ? 'All body lists have 3 or fewer items.'
    : `${listsOverCap.length} list${listsOverCap.length === 1 ? '' : 's'} exceed${listsOverCap.length === 1 ? 's' : ''} the 3-item cap.`;

  const strongCount = doc.querySelectorAll('strong').length;
  let strongStatus = FAIL;
  let strongPoints = 0;
  if (strongCount >= 3) { strongStatus = PASS; strongPoints = 3; }
  else if (strongCount >= 1) { strongStatus = WARN; strongPoints = 1; }
  const strongDetail = `${strongCount} bold key phrase${strongCount === 1 ? '' : 's'} for scanning.`;

  return buildSection(SECTIONS[5], [
    makeCheck('fmt-paragraphs', 'Short paragraphs (\u22643 sentences)', paraStatus, paraDetail, paraPoints, 5),
    makeCheck('fmt-lists', 'Uses lists/tables', listStatus, listDetail, lists.length >= 1 ? 4 : 0, 4),
    makeCheck('fmt-list-cap', 'Lists capped at 3 items', listCapStatus, listCapDetail, listCapPoints, 3),
    makeCheck('fmt-strong', 'Bold key phrases', strongStatus, strongDetail, strongPoints, 3),
  ]);
}

function scoreInternalLinks(doc) {
  const section = findSectionByIds(doc, INTERNAL_LINK_IDS);
  const presentStatus = section ? PASS : FAIL;
  const presentDetail = section
    ? 'A "further reading" internal-links section is present.'
    : 'No internal-links section found.';

  const anchors = section ? Array.from(section.querySelectorAll('a')) : [];
  const count = anchors.length;
  let countStatus = FAIL;
  let countPoints = 0;
  if (count >= 3 && count <= 5) { countStatus = PASS; countPoints = 3; }
  else if ((count >= 1 && count < 3) || (count > 5 && count <= 8)) { countStatus = WARN; countPoints = 1; }
  const countDetail = `${count} internal link${count === 1 ? '' : 's'} (ideal 3\u20135).`;

  const genericAnchor = anchors.some((a) => {
    const t = normalize(a.textContent);
    return t.includes('click here') || t.includes('klik hier');
  });
  const placeholder = anchors.length > 0 && anchors.every((a) => a.getAttribute('href') === '#');
  let qualityStatus = FAIL;
  let qualityPoints = 0;
  let qualityDetail = 'No internal links to evaluate.';
  if (anchors.length) {
    if (genericAnchor) {
      qualityStatus = FAIL;
      qualityDetail = 'Avoid generic anchor text like "click here" \u2014 use descriptive, keyword-rich text.';
    } else if (placeholder) {
      qualityStatus = WARN;
      qualityPoints = 1;
      qualityDetail = 'Anchor text is descriptive, but replace placeholder "#" URLs before publishing.';
    } else {
      qualityStatus = PASS;
      qualityPoints = 2;
      qualityDetail = 'Descriptive, keyword-rich anchor text with real URLs.';
    }
  }

  return buildSection(SECTIONS[6], [
    makeCheck('links-present', 'Internal-links section present', presentStatus, presentDetail, section ? 3 : 0, 3),
    makeCheck('links-count', '3\u20135 internal links', countStatus, countDetail, countPoints, 3),
    makeCheck('links-quality', 'Descriptive anchor text', qualityStatus, qualityDetail, qualityPoints, 2),
  ]);
}

function scoreFaq(doc) {
  const section = findSectionByIds(doc, FAQ_IDS);
  const presentStatus = section ? PASS : FAIL;
  const presentDetail = section ? 'An FAQ section is present.' : 'No FAQ section found.';

  const questions = section ? Array.from(section.querySelectorAll('h3')) : [];
  const count = questions.length;
  let countStatus = FAIL;
  let countPoints = 0;
  if (count >= 3 && count <= 5) { countStatus = PASS; countPoints = 4; }
  else if (count === 1 || count === 2 || (count > 5 && count <= 7)) { countStatus = WARN; countPoints = 2; }
  const countDetail = `${count} FAQ question${count === 1 ? '' : 's'} (ideal 3\u20135).`;

  const answers = section ? Array.from(section.querySelectorAll('p')) : [];
  const goodAnswers = answers.filter((p) => {
    const w = countWords(p.textContent);
    return w >= 30 && w <= 80;
  }).length;
  const answerRatio = answers.length ? goodAnswers / answers.length : 0;
  let answerStatus = FAIL;
  let answerPoints = 0;
  if (answers.length && answerRatio >= 0.6) { answerStatus = PASS; answerPoints = 4; }
  else if (answers.length && answerRatio >= 0.3) { answerStatus = WARN; answerPoints = 2; }
  const answerDetail = answers.length
    ? `${goodAnswers} of ${answers.length} answers land in the ~40\u201360 word snippet range.`
    : 'No FAQ answers to evaluate.';

  return buildSection(SECTIONS[8], [
    makeCheck('faq-present', 'FAQ section present', presentStatus, presentDetail, section ? 4 : 0, 4),
    makeCheck('faq-count', '3\u20135 questions', countStatus, countDetail, countPoints, 4),
    makeCheck('faq-answers', 'Concise 40\u201360 word answers', answerStatus, answerDetail, answerPoints, 4),
  ]);
}

function scoreConclusion(doc) {
  const section = findSectionByIds(doc, CONCLUSION_IDS);
  const presentStatus = section ? PASS : FAIL;
  const presentDetail = section
    ? 'A conclusion / key-takeaways section is present.'
    : 'No conclusion section found.';

  const paragraphs = section ? Array.from(section.querySelectorAll('p')) : [];
  const summaryStatus = paragraphs.length >= 1 ? PASS : FAIL;
  const summaryDetail = paragraphs.length
    ? 'Conclusion includes a summary paragraph.'
    : 'No summary paragraph in the conclusion.';

  const sectionText = section ? normalize(section.textContent) : '';
  const hasLink = section ? section.querySelectorAll('a').length > 0 : false;
  const hasCtaPhrase = CTA_PHRASES.some((phrase) => sectionText.includes(phrase));
  const hasCta = hasLink || hasCtaPhrase;
  const ctaStatus = hasCta ? PASS : FAIL;
  const ctaDetail = hasCta
    ? 'A call-to-action is present in the conclusion.'
    : 'No clear call-to-action found in the conclusion.';

  return buildSection(SECTIONS[7], [
    makeCheck('conclusion-present', 'Conclusion section present', presentStatus, presentDetail, section ? 3 : 0, 3),
    makeCheck('conclusion-summary', 'Summarizes the core answer', summaryStatus, summaryDetail, paragraphs.length >= 1 ? 2 : 0, 2),
    makeCheck('conclusion-cta', 'Includes a call-to-action', ctaStatus, ctaDetail, hasCta ? 2 : 0, 2),
  ]);
}

/**
 * The generation prompt appends a human note after the JSON-LD array
 * (e.g. "// replace values in [BRACKETS] before publishing") and may include
 * block comments. Strict JSON.parse rejects those, so trim to the outermost
 * array/object before parsing.
 */
function sanitizeJsonLd(raw) {
  let s = (raw || '').trim();
  if (!s) return s;
  // Strip block comments.
  s = s.replace(/\/\*[\s\S]*?\*\//g, '');
  // Strip line comments without clobbering the // in URLs (https://). Match
  // only at the start of a line or after JSON structural characters/whitespace.
  s = s.replace(/^\s*\/\/[^\n]*/gm, '');
  s = s.replace(/([\s\]},])\/\/[^\n]*/g, '$1');
  // Trim to the outermost array/object so trailing notes are dropped.
  const starts = ['[', '{'].map((c) => s.indexOf(c)).filter((i) => i >= 0);
  const ends = [']', '}'].map((c) => s.lastIndexOf(c)).filter((i) => i >= 0);
  if (starts.length && ends.length) {
    const start = Math.min(...starts);
    const end = Math.max(...ends);
    if (end >= start) s = s.slice(start, end + 1);
  }
  return s.trim();
}

function detectSchemaTypes(raw) {
  const types = new Set();
  const re = /"@type"\s*:\s*"([^"]+)"/g;
  let match;
  while ((match = re.exec(raw)) !== null) types.add(match[1]);
  return types;
}

function scoreSchema(schemaMarkup, doc) {
  let raw = schemaMarkup;
  if (!raw && doc) {
    const script = doc.querySelector('script[type="application/ld+json"]');
    raw = script ? script.textContent : '';
  }
  raw = (raw || '').trim();

  let parsed = null;
  let valid = false;
  if (raw) {
    try {
      parsed = JSON.parse(sanitizeJsonLd(raw));
      valid = true;
    } catch (err) {
      valid = false;
    }
  }
  const validStatus = valid ? PASS : FAIL;
  const validDetail = !raw
    ? 'No JSON-LD schema block found.'
    : valid
      ? 'JSON-LD schema parses correctly.'
      : 'A schema block exists but contains invalid JSON-LD.';

  const foundTypes = new Set();
  if (valid) {
    const objects = Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
    objects.forEach((obj) => {
      const type = obj && obj['@type'];
      if (typeof type === 'string') foundTypes.add(type);
      else if (Array.isArray(type)) type.forEach((t) => foundTypes.add(t));
    });
  } else if (raw) {
    // Fall back to detecting intended @type values even if the JSON is malformed.
    detectSchemaTypes(raw).forEach((t) => foundTypes.add(t));
  }
  const present = SCHEMA_TYPES.filter((t) => foundTypes.has(t));
  const typePoints = Math.round((present.length / SCHEMA_TYPES.length) * 5 * 100) / 100;
  let typeStatus = FAIL;
  if (present.length === SCHEMA_TYPES.length) typeStatus = PASS;
  else if (present.length >= 2) typeStatus = WARN;
  const typeDetail = raw
    ? `Includes ${present.length} of 4 recommended types (${SCHEMA_TYPES.join(', ')}).`
    : 'Schema types could not be read.';

  return buildSection(SECTIONS[9], [
    makeCheck('schema-valid', 'Valid JSON-LD block', validStatus, validDetail, valid ? 3 : 0, 3),
    makeCheck('schema-types', 'Article, FAQ, Person, Breadcrumb', typeStatus, typeDetail, typePoints, 5),
  ]);
}

/**
 * Compute the full SEO anatomy score for a generated blog post.
 *
 * @param {Object} input
 * @param {string} input.html - Full generated HTML (title + meta + body + schema).
 * @param {string} [input.seoTitle] - Parsed SEO title (overrides title parsed from html).
 * @param {string} [input.seoDescription] - Parsed meta description.
 * @param {string} [input.schemaMarkup] - Parsed JSON-LD string (without <script> tags).
 * @param {string} [input.primaryKeyword] - Headline/H1 the keyword tokens are derived from.
 * @returns {{ overall: number, sections: Array, computedAt: string } | null}
 */
export function computeSeoAnatomyScore(input = {}) {
  const { html = '', seoTitle, seoDescription, schemaMarkup, primaryKeyword = '' } = input;
  const doc = getDocument(html);
  if (!doc) return null;

  const title = (seoTitle != null ? seoTitle : (doc.querySelector('title')?.textContent || '')).trim();
  const metaEl = doc.querySelector('meta[name="description"]');
  const description = (seoDescription != null ? seoDescription : (metaEl?.getAttribute('content') || '')).trim();
  const tokens = extractKeywordTokens(primaryKeyword);

  const sections = [
    scoreTitle(title, tokens),
    scoreMeta(description, tokens),
    scoreHook(doc, tokens),
    scoreToc(doc),
    scoreBody(doc),
    scoreFormatting(doc),
    scoreInternalLinks(doc),
    scoreConclusion(doc),
    scoreFaq(doc),
    scoreSchema(schemaMarkup, doc),
  ];

  const totalWeight = sections.reduce((sum, s) => sum + s.maxScore, 0);
  const earned = sections.reduce((sum, s) => sum + s.score, 0);
  const overall = totalWeight > 0 ? Math.round((earned / totalWeight) * 100) : 0;

  return { overall, sections, computedAt: new Date().toISOString() };
}

export const SEO_SECTION_META = SECTIONS;
